import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

const WishlistContext = createContext(null);

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const { isAuthenticated, token } = useAuth();
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load wishlist when authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      // Load from server for authenticated users
      const fetchWishlist = async () => {
        setLoading(true);
        try {
          const wishlist = await api.getWishlist();
          setWishlistItems(wishlist.items.map(item => item.product_id));
        } catch (error) {
          console.error('Failed to load wishlist:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchWishlist();
    } else {
      // Load from localStorage for guests
      const storedWishlist = localStorage.getItem('guest_wishlist');
      if (storedWishlist) {
        setWishlistItems(JSON.parse(storedWishlist));
      } else {
        setWishlistItems([]);
      }
    }
  }, [isAuthenticated, token]);

  const loadWishlist = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setLoading(true);
    try {
      const wishlist = await api.getWishlist();
      setWishlistItems(wishlist.items.map(item => item.product_id));
    } catch (error) {
      console.error('Failed to load wishlist:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const addToWishlist = useCallback(async (productId) => {
    if (isAuthenticated) {
      try {
        await api.addToWishlist(productId);
        setWishlistItems(prev => [...prev, productId]);
        return true;
      } catch (error) {
        if (error.message?.includes('already in wishlist')) {
          return false;
        }
        throw error;
      }
    } else {
      // Guest wishlist in localStorage
      const current = JSON.parse(localStorage.getItem('guest_wishlist') || '[]');
      if (!current.includes(productId)) {
        const updated = [...current, productId];
        localStorage.setItem('guest_wishlist', JSON.stringify(updated));
        setWishlistItems(updated);
      }
      return true;
    }
  }, [isAuthenticated]);

  const removeFromWishlist = useCallback(async (productId) => {
    if (isAuthenticated) {
      try {
        await api.removeFromWishlist(productId);
        setWishlistItems(prev => prev.filter(id => id !== productId));
        return true;
      } catch (error) {
        throw error;
      }
    } else {
      // Guest wishlist in localStorage
      const current = JSON.parse(localStorage.getItem('guest_wishlist') || '[]');
      const updated = current.filter(id => id !== productId);
      localStorage.setItem('guest_wishlist', JSON.stringify(updated));
      setWishlistItems(updated);
      return true;
    }
  }, [isAuthenticated]);

  const clearWishlist = useCallback(async () => {
    if (isAuthenticated) {
      try {
        await api.clearWishlist();
        setWishlistItems([]);
        return true;
      } catch (error) {
        throw error;
      }
    } else {
      localStorage.removeItem('guest_wishlist');
      setWishlistItems([]);
      return true;
    }
  }, [isAuthenticated]);

  const isInWishlist = useCallback((productId) => {
    return wishlistItems.includes(productId);
  }, [wishlistItems]);

  const toggleWishlist = useCallback(async (productId) => {
    if (isInWishlist(productId)) {
      return await removeFromWishlist(productId);
    } else {
      return await addToWishlist(productId);
    }
  }, [isInWishlist, addToWishlist, removeFromWishlist]);

  // Sync guest wishlist to server after login
  const syncGuestWishlist = useCallback(async () => {
    const guestWishlist = JSON.parse(localStorage.getItem('guest_wishlist') || '[]');
    if (guestWishlist.length > 0 && isAuthenticated) {
      for (const productId of guestWishlist) {
        try {
          await api.addToWishlist(productId);
        } catch (error) {
          // Ignore if already exists
        }
      }
      localStorage.removeItem('guest_wishlist');
      await loadWishlist();
    }
  }, [isAuthenticated, loadWishlist]);

  const value = {
    wishlistItems,
    wishlistCount: wishlistItems.length,
    loading,
    addToWishlist,
    removeFromWishlist,
    clearWishlist,
    isInWishlist,
    toggleWishlist,
    loadWishlist,
    syncGuestWishlist
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};

export default WishlistContext;
